// ** Modules
import React from 'react';

// ** UI
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

// ** Components
import DashboardConsultingPanel from './DashboardConsultingPanel';
import DashboardEmployeeAccessPanel from './DashboardEmployeeAccessPanel';
import DashboardEventsPanel from './DashboardEventsPanel';
import DashboardProfilePanel from './DashboardProfilePanel';
import DashboardShortcutPanel, { DashboardShortcutButton } from './DashboardShortcutPanel';

// ** Types
import type { DashboardProps } from './types';

const UserDashboard = ({
	welcomeText,
	profile,
	events,
	consulting,
	favorites,
	volunteer,
	showVolunteerSection = false,
	employeeAccess,
	isSuperAdmin = false,
	sx
}: DashboardProps) => {
	const [activeEventIndex, setActiveEventIndex] = React.useState(0);

	// Determine visibility
	const volunteerSection = showVolunteerSection ? volunteer : undefined;
	const hasRightStack = !!events || !!consulting;

	return (
		<Box sx={[{ pt: { xs: 4, md: 10 }, pb: { xs: 4, md: 10 } }, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}>
			<Typography variant="title-md" component="h1" sx={{ color: 'common.black', mb: { xs: 6, md: 8 } }}>
				{welcomeText}
			</Typography>

			<Box
				sx={{
					display: 'grid',
					gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
					gridAutoRows: 'min-content',
					gap: 4,
					alignItems: 'stretch'
				}}
			>
				{/* 1. Profile Panel (Left Column) */}
				{profile && (
					<DashboardProfilePanel
						profile={profile}
						sx={{
							height: '100%',
							gridRow: { md: !volunteerSection ? 'span 2' : '1' }
						}}
					/>
				)}

				{/* 2. Events & Consulting Stack (Right Column) */}
				{hasRightStack && (
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							gap: 4,
							height: '100%',
							gridColumn: { md: 2 },
							gridRow: { md: !favorites ? 'span 2' : '1' }
						}}
					>
						{events && (
							<DashboardEventsPanel
								events={events}
								activeEventIndex={activeEventIndex}
								setActiveEventIndex={setActiveEventIndex}
							/>
						)}
						{consulting && (
							<DashboardConsultingPanel
								consulting={!volunteerSection ? { ...consulting, defaultExpanded: true } : consulting}
							/>
						)}
					</Box>
				)}

				{/* 3. Volunteer (Bottom Left) */}
				{volunteerSection && (
					<DashboardShortcutPanel
						section={volunteerSection}
						sx={{
							display: { xs: 'none', md: 'block' },
							gridRow: { md: 2 },
							gridColumn: { md: 1 }
						}}
					/>
				)}

				{/* 4. Favorites (Bottom Right) */}
				{favorites && (
					<DashboardShortcutPanel
						section={favorites}
						sx={{
							display: { xs: 'none', md: 'block' },
							gridRow: { md: 2 },
							gridColumn: { md: !hasRightStack ? 1 : 2 }
						}}
					/>
				)}

				{/* Mobile Shortcut Buttons */}
				<Box
					sx={{
						display: { xs: 'grid', md: 'none' },
						gridTemplateColumns: volunteerSection && favorites ? '1fr 1fr' : '1fr',
						gap: 4
					}}
				>
					{volunteerSection && <DashboardShortcutButton section={volunteerSection} />}
					{favorites && <DashboardShortcutButton section={favorites} />}
				</Box>
			</Box>

			{isSuperAdmin && employeeAccess ? <DashboardEmployeeAccessPanel section={employeeAccess} sx={{ mt: 4 }} /> : null}
		</Box>
	);
};

export default UserDashboard;
